<template>
  <div class="p-4">
    <h1 class="text-2xl font-bold mb-4">سجل رفع التسجيلات</h1>

    <div v-if="summary" class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <div class="border rounded p-3 bg-white">
        <div class="text-xs text-gray-500">إجمالي</div>
        <div class="text-2xl font-bold">{{ summaryTotal }}</div>
      </div>
      <div class="border rounded p-3 bg-green-50">
        <div class="text-xs text-gray-500">ناجح</div>
        <div class="text-2xl font-bold text-green-700">{{ summary.byStatus?.success || 0 }}</div>
      </div>
      <div class="border rounded p-3 bg-red-50">
        <div class="text-xs text-gray-500">فشل</div>
        <div class="text-2xl font-bold text-red-700">{{ summary.byStatus?.failed || 0 }}</div>
      </div>
      <div class="border rounded p-3 bg-yellow-50">
        <div class="text-xs text-gray-500">قيد التشغيل</div>
        <div class="text-2xl font-bold text-yellow-700">{{ summary.byStatus?.started || 0 }}</div>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 mb-4 items-end bg-gray-50 p-3 rounded border">
      <div>
        <label class="block mb-1 text-sm">الحالة</label>
        <select v-model="filters.status" class="border px-3 py-2 rounded w-40">
          <option value="">الكل</option>
          <option value="success">نجاح</option>
          <option value="failed">فشل</option>
          <option value="started">بدأ</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">المرحلة</label>
        <select v-model="filters.stage" class="border px-3 py-2 rounded w-44">
          <option value="">الكل</option>
          <option value="validation">تحقق (validation)</option>
          <option value="s3_upload">رفع S3</option>
          <option value="db_insert">حفظ DB</option>
          <option value="fallback_local">حفظ محلي احتياطي</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">نوع الجلسة</label>
        <select v-model="filters.sessionType" class="border px-3 py-2 rounded w-48">
          <option value="">الكل</option>
          <option value="iqra_slot_teacher">إقراء (iqra_slot_teacher)</option>
          <option value="iqra_live_session">إقراء - تسجيل سحابي (iqra_live_session)</option>
          <option value="mudarasa_tracker">مدارسة (mudarasa_tracker)</option>
          <option value="peer_review_tracker">تعاهد (peer_review_tracker)</option>
          <option value="daily_tracker">يومي (daily_tracker)</option>
        </select>
      </div>
      <div>
        <label class="block mb-1 text-sm">معرف الجلسة</label>
        <input v-model="filters.sessionId" type="number" class="border px-3 py-2 rounded w-32" placeholder="—" />
      </div>
      <div>
        <label class="block mb-1 text-sm">معرف المستخدم</label>
        <input v-model="filters.userId" type="number" class="border px-3 py-2 rounded w-32" placeholder="—" />
      </div>
      <div>
        <label class="block mb-1 text-sm">من تاريخ</label>
        <input v-model="filters.dateFrom" type="datetime-local" class="border px-3 py-2 rounded" />
      </div>
      <div>
        <label class="block mb-1 text-sm">إلى تاريخ</label>
        <input v-model="filters.dateTo" type="datetime-local" class="border px-3 py-2 rounded" />
      </div>
      <div class="flex-1 min-w-[200px]">
        <label class="block mb-1 text-sm">بحث (الخطأ / الكود / اسم الملف / مفتاح S3)</label>
        <input v-model="filters.search" type="text" class="border px-3 py-2 rounded w-full" placeholder="نص للبحث" />
      </div>
      <button @click="search" class="bg-blue-600 text-white px-4 py-2 rounded">بحث</button>
      <button @click="resetFilters" class="bg-gray-200 px-4 py-2 rounded">إزالة الفلاتر</button>
      <button @click="load" class="bg-white border px-4 py-2 rounded" title="تحديث">↻</button>
    </div>

    <div v-if="loading" class="py-6 text-center text-gray-500">جاري التحميل...</div>
    <div v-else>
      <div v-if="logs.length === 0" class="py-6 text-gray-500 text-center">لا توجد بيانات</div>

      <div v-else class="overflow-x-auto">
        <table class="w-full border text-sm bg-white">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="p-2 border whitespace-nowrap">الوقت</th>
              <th class="p-2 border">الحالة</th>
              <th class="p-2 border">المرحلة</th>
              <th class="p-2 border">الجلسة</th>
              <th class="p-2 border">المستخدم</th>
              <th class="p-2 border">الملف</th>
              <th class="p-2 border">الخطأ</th>
              <th class="p-2 border">إجراء</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in logs" :key="item.id">
              <tr :class="rowClass(item.status)">
                <td class="p-2 border whitespace-nowrap text-xs">{{ formatDate(item.createdAt) }}</td>
                <td class="p-2 border whitespace-nowrap">
                  <span :class="statusBadgeClass(item.status)">{{ statusLabel(item.status) }}</span>
                </td>
                <td class="p-2 border whitespace-nowrap text-xs">{{ stageLabel(item.stage) }}</td>
                <td class="p-2 border text-xs">
                  <div class="text-gray-700">{{ item.sessionType || '—' }}</div>
                  <div class="text-gray-500">#{{ item.sessionId || '—' }}</div>
                </td>
                <td class="p-2 border text-xs">
                  <div>{{ item.userFullName || item.username || '—' }}</div>
                  <div class="text-gray-500">{{ item.userId ? '#' + item.userId : '' }}</div>
                </td>
                <td class="p-2 border text-xs">
                  <div :title="item.fileName">{{ truncate(item.fileName, 28) || '—' }}</div>
                  <div class="text-gray-500">{{ formatBytes(item.fileSize) }} <span v-if="item.fileMime">· {{ item.fileMime }}</span></div>
                </td>
                <td class="p-2 border text-xs max-w-md">
                  <div v-if="item.errorCode" class="font-mono text-red-700">{{ item.errorCode }}</div>
                  <div v-if="item.errorMessage" class="text-gray-700 line-clamp-2">{{ item.errorMessage }}</div>
                  <div v-if="!item.errorCode && !item.errorMessage" class="text-gray-400">—</div>
                </td>
                <td class="p-2 border whitespace-nowrap">
                  <button class="text-blue-600 text-xs" @click="toggle(item)">{{ item._expanded ? 'إخفاء' : 'تفاصيل' }}</button>
                </td>
              </tr>
              <tr v-if="item._expanded" class="bg-gray-50">
                <td colspan="8" class="p-3 border">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div class="font-semibold mb-1">معلومات الصف</div>
                      <table class="w-full text-xs border">
                        <tbody>
                          <tr><td class="p-1 border bg-gray-100 w-32">ID</td><td class="p-1 border">{{ item.id }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">tenant_id</td><td class="p-1 border font-mono">{{ item.tenantId || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">recording_id</td><td class="p-1 border">{{ item.recordingId || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">s3_object_key</td><td class="p-1 border font-mono break-all">{{ item.s3ObjectKey || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">file_name</td><td class="p-1 border break-all">{{ item.fileName || '—' }}</td></tr>
                          <tr><td class="p-1 border bg-gray-100">file_size</td><td class="p-1 border">{{ formatBytes(item.fileSize) }} ({{ item.fileSize || 0 }} B)</td></tr>
                          <tr><td class="p-1 border bg-gray-100">file_mime</td><td class="p-1 border">{{ item.fileMime || '—' }}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <div class="font-semibold mb-1">رسالة الخطأ الكاملة</div>
                      <pre class="text-xs bg-red-50 border p-2 rounded overflow-auto" style="max-height: 220px;">{{ item.errorMessage || '—' }}</pre>
                      <div class="font-semibold mb-1 mt-3">Meta</div>
                      <pre class="text-xs bg-white border p-2 rounded overflow-auto" style="max-height: 220px;">{{ pretty(item.meta) }}</pre>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between mt-4">
        <div class="text-sm text-gray-600">
          صفحة {{ currentPage + 1 }} من {{ totalPages || 1 }} — إجمالي {{ totalElements }}
        </div>
        <div class="flex items-center gap-2">
          <button :disabled="currentPage === 0" @click="prev" class="px-3 py-1 border rounded disabled:opacity-50">السابق</button>
          <button :disabled="currentPage >= totalPages - 1" @click="next" class="px-3 py-1 border rounded disabled:opacity-50">التالي</button>
          <select v-model.number="pageSize" @change="reload" class="border px-2 py-1 rounded">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          <select v-model="direction" @change="reload" class="border px-2 py-1 rounded">
            <option value="desc">الأحدث أولاً</option>
            <option value="asc">الأقدم أولاً</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '../url';

export default {
  name: 'RecordingsLogs',
  data() {
    return {
      logs: [],
      summary: null,
      loading: false,
      currentPage: 0,
      pageSize: 20,
      totalPages: 0,
      totalElements: 0,
      direction: 'desc',
      filters: {
        status: '',
        stage: '',
        sessionType: '',
        sessionId: '',
        userId: '',
        search: '',
        dateFrom: '',
        dateTo: '',
      },
    };
  },
  computed: {
    summaryTotal() {
      if (!this.summary?.byStatus) return 0;
      return Object.values(this.summary.byStatus).reduce((a, b) => a + Number(b || 0), 0);
    },
  },
  mounted() {
    this.load();
    this.loadSummary();
  },
  methods: {
    buildParams() {
      const f = this.filters;
      return {
        page: this.currentPage,
        size: this.pageSize,
        direction: this.direction,
        status: f.status || undefined,
        stage: f.stage || undefined,
        sessionType: f.sessionType || undefined,
        sessionId: f.sessionId === '' ? undefined : f.sessionId,
        userId: f.userId === '' ? undefined : f.userId,
        search: f.search?.trim() || undefined,
        dateFrom: f.dateFrom || undefined,
        dateTo: f.dateTo || undefined,
      };
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.recordings_logs, {
          params: this.buildParams(),
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        const data = res.data?.result?.data || res.data?.data;
        this.logs = (data?.content || []).map(x => ({ ...x, _expanded: false }));
        this.totalElements = data?.totalElements || 0;
        this.totalPages = data?.totalPages || 0;
        this.currentPage = data?.currentPage || 0;
        this.pageSize = data?.pageSize || this.pageSize;
      } catch (e) {
        console.error('Failed loading recordings logs', e);
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },
    async loadSummary() {
      try {
        const params = {
          dateFrom: this.filters.dateFrom || undefined,
          dateTo: this.filters.dateTo || undefined,
        };
        const res = await axios.get(process.env.VUE_APP_SERVICE_URL + url.recordings_logs_summary, {
          params,
          headers: { 'X-Authorization': localStorage.getItem('token') },
        });
        this.summary = res.data?.result?.data || res.data?.data || null;
      } catch (e) {
        console.error('Failed loading summary', e);
        this.summary = null;
      }
    },
    search() {
      this.currentPage = 0;
      this.load();
      this.loadSummary();
    },
    resetFilters() {
      this.filters = {
        status: '', stage: '', sessionType: '', sessionId: '', userId: '',
        search: '', dateFrom: '', dateTo: '',
      };
      this.currentPage = 0;
      this.load();
      this.loadSummary();
    },
    prev() { if (this.currentPage > 0) { this.currentPage--; this.load(); } },
    next() { if (this.currentPage < this.totalPages - 1) { this.currentPage++; this.load(); } },
    reload() { this.currentPage = 0; this.load(); },
    toggle(item) { item._expanded = !item._expanded; },
    formatDate(iso) {
      if (!iso) return '—';
      try { return new Date(iso).toLocaleString('ar-SA'); } catch { return iso; }
    },
    formatBytes(b) {
      if (b == null) return '—';
      const n = Number(b);
      if (!Number.isFinite(n) || n <= 0) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      let v = n;
      while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
      return v.toFixed(v >= 10 || i === 0 ? 0 : 1) + ' ' + units[i];
    },
    truncate(s, n) {
      if (!s) return '';
      return s.length > n ? s.slice(0, n - 1) + '…' : s;
    },
    pretty(json) {
      if (!json) return '—';
      try {
        const obj = typeof json === 'string' ? JSON.parse(json) : json;
        return JSON.stringify(obj, null, 2);
      } catch { return String(json); }
    },
    statusLabel(s) {
      return { success: 'نجاح', failed: 'فشل', started: 'بدأ' }[s] || s;
    },
    stageLabel(s) {
      return {
        validation: 'تحقق',
        s3_upload: 'رفع S3',
        db_insert: 'حفظ DB',
        fallback_local: 'محلي',
      }[s] || s;
    },
    statusBadgeClass(s) {
      const base = 'px-2 py-1 rounded text-white text-xs';
      if (s === 'success') return base + ' bg-green-600';
      if (s === 'failed') return base + ' bg-red-600';
      if (s === 'started') return base + ' bg-yellow-600';
      return base + ' bg-gray-600';
    },
    rowClass(s) {
      if (s === 'failed') return 'bg-red-50';
      if (s === 'started') return 'bg-yellow-50';
      return '';
    },
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
